/**
 * Event orders page: detailed view of orders for a specific event.
 * Shows event stats, order list with filters, and quick actions.
 */

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
  useMemo,
} from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import { Loader } from "../../../components/Loader.tsx";
import {
  IconDocument,
  IconTicket,
  IconClock,
  IconAlertTriangle,
  IconCalendar,
} from "../../../components/Icons.tsx";
import { getTelegram, safeShowAlert } from "../../../lib/telegram.ts";
import { useDebouncedValue } from "../../../lib/use-debounce.ts";
import {
  formatDateBangkok,
  formatTimeBangkok,
} from "../../../lib/bangkok-time.ts";
// AdminOrder type is used by useEventOrders
import { OrderCard } from "./components/OrderCard.tsx";
import { OrderFilters } from "./components/OrderFilters.tsx";
import {
  OrderStatusSlider,
  type OrderLifecycleStatus,
  getBackendStatusFilter,
  matchesVerifiedFilter,
  isValidLifecycleStatus,
} from "./components/OrderStatusSlider.tsx";
import { useAdminEventDetails } from "./hooks/useAdminEventDetails";
import { useEventOrders } from "../shared/hooks/useAdminOrders";
import {
  useGenerateTickets,
  useResendTickets,
} from "../tickets/hooks/useTicketMutations";

/** Session storage keys for state restoration from ticket view */
const SCROLL_STORAGE_KEY = "admin_ticket_view_scroll";
const EXPANDED_ORDER_STORAGE_KEY = "admin_ticket_view_expanded_order";

const LIFECYCLE_FILTER_STORAGE_KEY = "admin_orders_lifecycle_filter";

interface EventStats {
  ordersCount: number;
  ticketsCount: number;
  pendingCount: number;
}

function StatCard({
  icon,
  value,
  label,
  highlight = false,
}: {
  icon: React.ReactNode;
  value: number;
  label: string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`flex flex-col items-center justify-center py-3 px-2 rounded-xl ${
        highlight
          ? "bg-amber-50 border border-amber-200"
          : "bg-white/95 shadow-sm"
      }`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center mb-1.5 ${
          highlight
            ? "bg-amber-100 text-amber-600"
            : "bg-slate-100 text-slate-500"
        }`}
      >
        {icon}
      </div>
      <span
        className={`text-xl font-bold ${
          highlight ? "text-amber-700" : "text-slate-900"
        }`}
      >
        {value}
      </span>
      <span className="text-[10px] text-slate-500 text-center leading-tight">
        {label}
      </span>
    </div>
  );
}

export function EventOrdersPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();

  // URQL hooks for data fetching
  const {
    event,
    isLoading: isEventLoading,
    error: eventError,
  } = useAdminEventDetails({ eventId: eventId ?? "" });

  const {
    orders: allOrders,
    totalCount,
    isLoading: isOrdersLoading,
    error: ordersError,
    filter,
    setFilter,
    refresh: refreshOrders,
  } = useEventOrders(eventId ?? "");

  // URQL mutation hooks
  const { generateTickets } = useGenerateTickets();
  const { resendTickets } = useResendTickets();

  // Combined loading and error states
  const isLoading = isEventLoading || isOrdersLoading;
  const error = eventError?.message ?? ordersError ?? null;

  const [lifecycleStatus, setLifecycleStatus] =
    useState<OrderLifecycleStatus>(() => {
      const saved = sessionStorage.getItem(LIFECYCLE_FILTER_STORAGE_KEY);
      return isValidLifecycleStatus(saved) ? saved : "all";
    });
  const [searchInput, setSearchInput] = useState<string>("");
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [processingOrderId, setProcessingOrderId] = useState<string | null>(
    null
  );
  const [processingAction, setProcessingAction] = useState<
    "generate" | "resend" | null
  >(null);

  // Debounce search for API calls (50ms)
  const searchQuery = useDebouncedValue(searchInput, 50);
  
  // Update filter when search changes
  useEffect(() => {
    setFilter({ ...filter, search: searchQuery.trim() || undefined });
  }, [searchQuery]);

  // Hide content until scroll restoration is complete (prevents visual jump)
  const [isScrollRestored, setIsScrollRestored] = useState(true);

  // Track scroll restoration state
  const scrollRestorationRef = useRef<{
    orderId: string | null;
    needsRestore: boolean;
  }>({ orderId: null, needsRestore: false });

  // Read from sessionStorage ONCE on mount (useLayoutEffect runs before paint)
  useLayoutEffect(() => {
    const savedOrderId = sessionStorage.getItem(EXPANDED_ORDER_STORAGE_KEY);

    // Clear storage immediately
    sessionStorage.removeItem(SCROLL_STORAGE_KEY);
    sessionStorage.removeItem(EXPANDED_ORDER_STORAGE_KEY);

    if (savedOrderId) {
      scrollRestorationRef.current = {
        orderId: savedOrderId,
        needsRestore: true,
      };
      // Hide content until scroll is restored
      setIsScrollRestored(false);
      // Set expanded order immediately
      setExpandedOrderId(savedOrderId);
    }
  }, []);

  // Save filter to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(LIFECYCLE_FILTER_STORAGE_KEY, lifecycleStatus);
  }, [lifecycleStatus]);

  // Toggle card expansion (only one at a time)
  const handleToggleOrder = useCallback((orderId: string) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  }, []);

  const handleGenerate = useCallback(
    async (orderId: string) => {
      setProcessingOrderId(orderId);
      setProcessingAction("generate");
      try {
        const result = await generateTickets(orderId);
        if (result) {
          const msg = result.message ?? "Билеты сгенерированы";
          safeShowAlert(msg);
          refreshOrders();
        }
      } finally {
        setProcessingOrderId(null);
        setProcessingAction(null);
      }
    },
    [generateTickets, refreshOrders]
  );

  const handleResend = useCallback(
    async (orderId: string) => {
      setProcessingOrderId(orderId);
      setProcessingAction("resend");
      try {
        const result = await resendTickets(orderId);
        if (result) {
          const msg = result.message ?? "Билеты отправлены";
          safeShowAlert(msg);
          refreshOrders();
        }
      } finally {
        setProcessingOrderId(null);
        setProcessingAction(null);
      }
    },
    [resendTickets, refreshOrders]
  );

  // Setup BackButton - navigate to parent event page
  useEffect(() => {
    if (!tg) return;

    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(`/admin/events/${eventId}`);
      }
    };

    tg.BackButton.show();
    tg.BackButton.onClick(handleBack);

    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, eventId]);

  // Filter orders client-side based on lifecycle status
  const orders = useMemo(() => {
    if (lifecycleStatus === "all") {
      return allOrders;
    }
    if (lifecycleStatus === "rejected") {
      return allOrders.filter(
        (order) =>
          order.status === "REJECTED" ||
          order.status === "CANCELLED" ||
          order.status === "EXPIRED"
      );
    }
    // Map lifecycle status to backend status for filtering
    const backendStatus = getBackendStatusFilter(lifecycleStatus);
    if (backendStatus) {
      if (lifecycleStatus === "verified") {
        return allOrders.filter((o) => matchesVerifiedFilter(o.status));
      }
      return allOrders.filter((order) => order.status === backendStatus);
    }
    return allOrders;
  }, [allOrders, lifecycleStatus]);

  // Calculate counts for each status from all orders
  const statusCounts = useMemo(() => {
    const counts: Partial<Record<OrderLifecycleStatus, number>> = {
      all: totalCount,
      awaiting_payment: 0,
      pending_verification: 0,
      verified: 0,
      ticket_issued: 0,
      rejected: 0,
    };

    for (const order of allOrders) {
      switch (order.status) {
        case "AWAITING_PAYMENT":
          counts.awaiting_payment = (counts.awaiting_payment ?? 0) + 1;
          break;
        case "PENDING_VERIFICATION":
          counts.pending_verification = (counts.pending_verification ?? 0) + 1;
          break;
        case "VERIFIED":
          counts.verified = (counts.verified ?? 0) + 1;
          break;
        case "TICKET_ISSUED":
          counts.ticket_issued = (counts.ticket_issued ?? 0) + 1;
          break;
        case "TICKETS_PENDING_SEND":
          counts.verified = (counts.verified ?? 0) + 1;
          break;
        case "REJECTED":
        case "CANCELLED":
        case "EXPIRED":
          counts.rejected = (counts.rejected ?? 0) + 1;
          break;
      }
    }

    return counts;
  }, [allOrders, totalCount]);

  // Restore scroll: scroll to expanded card after data loads
  useLayoutEffect(() => {
    const restoration = scrollRestorationRef.current;

    // Skip if no restoration needed
    if (!restoration.needsRestore) return;
    if (!restoration.orderId) return;

    // Skip if still loading
    if (isLoading) return;

    // Skip if no orders yet
    if (orders.length === 0) return;

    // Mark as done to prevent re-runs
    restoration.needsRestore = false;

    const targetOrderId = restoration.orderId;

    // Poll for the card to appear in DOM and scroll to it
    let attempts = 0;
    const maxAttempts = 30;

    const checkAndScroll = () => {
      const card = document.querySelector(`[data-order-id="${targetOrderId}"]`);
      if (card) {
        // Card found - scroll it into view (center of screen)
        card.scrollIntoView({ block: "center", behavior: "instant" });
        // Show content after scroll
        setIsScrollRestored(true);
      } else if (attempts < maxAttempts) {
        attempts++;
        requestAnimationFrame(checkAndScroll);
      } else {
        // Give up, show content anyway
        setIsScrollRestored(true);
      }
    };

    // Start checking immediately
    checkAndScroll();
  }, [isLoading, orders.length]);

  // Calculate stats from orders
  const stats: EventStats = {
    ordersCount: totalCount,
    ticketsCount: allOrders.reduce(
      (sum, order) => sum + order.tickets.length,
      0
    ),
    pendingCount: allOrders.filter(
      (order) => order.status === "PENDING_VERIFICATION"
    ).length,
  };

  if (isLoading && !event) {
    return (
      <Layout>
        <PageLoader message="Загрузка..." />
      </Layout>
    );
  }

  if (error && !event) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
          <IconAlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
          <p className="text-slate-600 text-[15px] mb-6">{error}</p>
          <button onClick={refreshOrders} className="btn-primary">
            Повторить
          </button>
        </div>
      </Layout>
    );
  }

  if (!event) return null;

  const dateStr = formatDateBangkok(event.startsAt, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const timeStr = formatTimeBangkok(event.startsAt);
  const hasActiveFilters = !!(lifecycleStatus !== "all" || searchInput.trim());

  return (
    <Layout>
      <div
        style={{
          opacity: isScrollRestored ? 1 : 0,
          transition: isScrollRestored ? "opacity 0.15s ease-out" : "none",
        }}
      >
        {/* Event header */}
        <div className="card p-4 mb-4">
          <div className="flex items-start gap-3">
            {/* Event image */}
            <div className="w-16 h-16 rounded-xl bg-slate-100 overflow-hidden shrink-0">
              {event.imageUrl ? (
                <img
                  src={event.imageUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <IconCalendar className="w-7 h-7" />
                </div>
              )}
            </div>

            {/* Event info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-[17px] font-bold text-slate-900 line-clamp-2">
                {event.title}
              </h1>
              <p className="text-[13px] text-slate-500 mt-1">
                {dateStr} • {timeStr}
              </p>
              {event.venue && (
                <p className="text-[12px] text-slate-400 mt-0.5 truncate">
                  {event.venue}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <StatCard
            icon={<IconDocument className="w-4 h-4" />}
            value={stats.ordersCount}
            label="Заказов"
          />
          <StatCard
            icon={<IconTicket className="w-4 h-4" />}
            value={stats.ticketsCount}
            label="Билетов"
          />
          <StatCard
            icon={<IconClock className="w-4 h-4" />}
            value={stats.pendingCount}
            label="На проверке"
            highlight={stats.pendingCount > 0}
          />
        </div>

        {/* Quick links */}
        <div className="flex gap-2 mb-4">
          <Link
            to={`/admin/tickets?eventId=${eventId}`}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-100 text-slate-700 font-medium text-[14px] active:bg-slate-200"
          >
            <IconTicket className="w-4 h-4" />
            Билеты
          </Link>
          <Link
            to={`/admin/tickets/validate?eventId=${eventId}`}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-teal-500 text-white font-medium text-[14px] active:opacity-90"
          >
            <IconTicket className="w-4 h-4" />
            Сканировать
          </Link>
        </div>

        {/* Status Slider */}
        <OrderStatusSlider
          value={lifecycleStatus}
          onChange={setLifecycleStatus}
          counts={statusCounts}
        />

        {/* Search */}
        <OrderFilters
          searchQuery={searchInput}
          onSearchChange={setSearchInput}
          isLoading={isLoading}
          onRefresh={refreshOrders}
          showSearch={true}
          searchPlaceholder="Поиск по заказу или пользователю"
        />

        {/* Results count */}
        {(hasActiveFilters || orders.length === 0) && !isLoading && (
          <div className="flex items-center justify-between mb-3 min-h-[20px]">
            <span className="text-[12px] text-slate-400">
              {orders.length === 0
                ? "Нет заказов"
                : totalCount > orders.length
                ? `${orders.length} из ${totalCount}`
                : `${orders.length}`}
            </span>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={() => {
                  setLifecycleStatus("all");
                  setSearchInput("");
                }}
                className="text-[13px] font-medium text-[var(--color-accent)] active:opacity-80"
              >
                Сбросить
              </button>
            )}
          </div>
        )}

        {/* Orders list */}
        {orders.length === 0 && !isLoading ? (
          <div className="py-12 px-4 text-center">
            <IconDocument className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-[15px] text-slate-500">Заказы не найдены</p>
          </div>
        ) : (
          <div className="-mx-3 xs:-mx-4 min-[430px]:-mx-5 min-[520px]:-mx-6 sm:-mx-7">
            {orders.map((order) => (
              <OrderCard
                key={order.id}
                order={order}
                showEvent={false}
                isExpanded={expandedOrderId === order.id}
                onToggle={handleToggleOrder}
                onGenerate={handleGenerate}
                onResend={handleResend}
                isProcessing={processingOrderId === order.id}
              />
            ))}
          </div>
        )}

        {/* Bottom spacing */}
        <div className="h-4" />
      </div>

      {/* Processing overlay - генерация / отправка */}
      {processingOrderId && processingAction && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center">
          <div className="text-center">
            <Loader className="mx-auto mb-4" />
            <p className="text-white text-[16px] font-medium">
              {processingAction === "generate"
                ? "Генерация билетов..."
                : "Отправка..."}
            </p>
            <p className="text-white/60 text-[13px] mt-1">
              {processingAction === "generate"
                ? "Создание изображений билетов"
                : "Отправка билетов пользователю"}
            </p>
          </div>
        </div>
      )}
    </Layout>
  );
}
